import { Box, lighten, Stack, Typography } from "@mui/material";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import FilledIcon from "../../../../components/FIlledIcon";
import {
  BG_COLORS,
  BORDER_COLORS,
  FONT_COLORS,
  STATUS_COLORS,
} from "../../../../constants";
import { formatCurrency } from "../../../../helpers";
import { TPricingPlan } from "../../../../features/pricingPlans/pricingPlans.types";
import { TUserCurrentPlan } from "../../../../features/user/user.types";
import moment from "moment";
import { useAppSelector } from "../../../../store/hooks";
import { selectUserProfile } from "../../../../features/user/user.selector";

export type TCurrentPlanCardProps = {
  plan: TPricingPlan;
  nextPaymentDate: TUserCurrentPlan["nextPaymentDate"] | undefined;
  nextPlanId: TUserCurrentPlan["nextPlanId"] | undefined;
};

export const CurrentPlanCard = ({
  plan,
  nextPaymentDate,
  nextPlanId,
}: TCurrentPlanCardProps) => {
  const { productAnalyzer } = plan.features;

  const user = useAppSelector(selectUserProfile);
  const subscriptionState = user?.subscriptionState;
  const pricingPlans = useAppSelector((state) => state.pricingPlans.data);

  const nextPlan = pricingPlans.find((p) => p.id === nextPlanId);

  const lookups =
    productAnalyzer.lookups === -1
      ? "Unlimited Lookups"
      : `${productAnalyzer.lookups} Lookups Per Month`;
  const webApp = productAnalyzer.webApp ? "Web App" : null;
  const chromeExtension = productAnalyzer.chromeExtension
    ? "Chrome Extension"
    : null;

  const features = [lookups, webApp, chromeExtension]
    .filter(Boolean)
    .join(", ");

  const price = formatCurrency(
    plan.period === "monthly" ? plan.price : plan.price * 12,
  );
  const formattedNextPaymentDate =
    moment(nextPaymentDate).format("MMM DD, YYYY");

  const paymentFail = subscriptionState === "past_due" || subscriptionState === "unpaid";

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      gap={3}
      px={3}
      py={2}
      border={`1px solid ${paymentFail ? BORDER_COLORS.error : BORDER_COLORS.info}`}
      borderRadius={1}
      bgcolor={lighten(paymentFail ? BG_COLORS.error : BG_COLORS.primary, 0.95)}
    >
      <BusinessCenterIcon sx={{ color: lighten(FONT_COLORS.default, 0.5) }} />
      <Stack flex={1} gap={0.5}>
        <Stack
          flexDirection="row"
          alignItems="center"
          gap={1.5}
          flexWrap="wrap"
        >
          <Typography
            fontSize={14}
            fontWeight={600}
            textTransform="capitalize"
            color={FONT_COLORS.default}
          >
            {plan.name} {plan.period}
          </Typography>
          {plan.period && (
            <Typography
              fontSize={12}
              fontWeight={600}
              color={paymentFail ? FONT_COLORS.error : FONT_COLORS.info}
            >
              {price}/{plan.period === "monthly" ? "Month" : "Year"}
            </Typography>
          )}
        </Stack>
        {nextPaymentDate && !nextPlan && (
          <Typography
            fontSize={12}
            fontWeight={500}
            color={FONT_COLORS.subheading}
          >
            {subscriptionState === "trialing" && (
              <b>Trial ends on {formattedNextPaymentDate}</b>
            )}

            {subscriptionState === "active" && (
              <>
                <b>Next payment on: </b>
                {formattedNextPaymentDate}
              </>
            )}
          </Typography>
        )}
        <Typography
          fontSize={12}
          fontWeight={500}
          color={FONT_COLORS.subheading}
        >
          <b>Included:</b> {features}
        </Typography>
        {nextPlan && (
          <Typography fontSize={10} fontWeight={600} color={FONT_COLORS.info}>
            The plan was switched to
            <Typography
              fontSize="inherit"
              fontWeight={800}
              color="inherit"
              component="span"
              textTransform="capitalize"
            >
              {!nextPlan.period
                ? ` ${nextPlan.name} `
                : ` ${nextPlan.name} ${nextPlan.period}. `}
            </Typography>
            You can access all features of your current plan until{" "}
            <Typography
              fontSize="inherit"
              fontWeight={800}
              color="inherit"
              component="span"
              textTransform="capitalize"
            >
              {formattedNextPaymentDate}
            </Typography>
          </Typography>
        )}
      </Stack>
        {paymentFail ? (
          <FilledIcon renderIcon={(props) => <CloseIcon {...props} />} success={false} />
        ) : (
          <FilledIcon renderIcon={(props) => <CheckIcon {...props} />} success={true} />
        )}
    </Stack>
  );
};
